import { EZoneCategory, SiteEntryProps, ZoneEntry } from '@ixicare/resources';
import { Button, Input, SubmitButton, LivingUnitSelect } from '@ixicare/ui';
import * as React from 'react';
import { ZoneCategorySelect } from '../components/ZoneCategorySelect';
import { useLivingUnitSelect } from '../hooks/useLivingUnitSelect';
import { t } from '~i18n';

interface DrawToolUpdateContainerProps {
  zone: ZoneEntry;
  site: SiteEntryProps;
  hasUpdatedGeo: boolean;
  onDelete: () => void;
  onUpdate: (props: { name: string; livingUnitId?: string; category?: EZoneCategory }) => void;
}

const DrawToolUpdateContainer: React.FC<DrawToolUpdateContainerProps> = ({
  site,
  zone,
  hasUpdatedGeo,
  onDelete,
  onUpdate,
}) => {
  const currentZone = React.useRef(zone);
  const [name, setName] = React.useState(zone.name);
  const [category, handleCategoryChange] = React.useState<EZoneCategory | undefined>(zone.category);

  const { options, livingUnits, livingUnitId, createLuMutation, handleCreateOption, onChange } = useLivingUnitSelect({
    site,
    initialValue: zone?.livingUnitId,
  });

  React.useEffect(() => {
    if (livingUnits.isLoading) {
      return;
    }
    if (currentZone.current.uuid !== zone.uuid) {
      currentZone.current = zone;
      onChange(zone.livingUnitId, zone.name);
      handleCategoryChange(zone.category);
      setName(zone.name);
    }
  }, [zone, livingUnits.isLoading]);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value);
  }, []);
  const handleSubmit = React.useCallback(
    (e) => {
      e.preventDefault();
      onUpdate({ name, livingUnitId, category });
    },
    [name, livingUnitId, category, onUpdate]
  );

  const isDisabled =
    zone.name === name && !hasUpdatedGeo && livingUnitId === zone.livingUnitId && category === zone.category;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="mb-15">
          <LivingUnitSelect
            options={options}
            livingUnitId={livingUnitId}
            isLoading={livingUnits.isLoading || createLuMutation.isPending}
            onCreateOption={handleCreateOption}
            onChange={onChange}
          />
        </div>
        <div className="mb-15">
          <ZoneCategorySelect category={category} onChange={handleCategoryChange} />
        </div>
        <div className="mb-15">
          <SubmitButton disabled={isDisabled} isBlock>
            {t('drawtool.sidepanel.button.update')}
          </SubmitButton>
        </div>
        <div className="mb-15">
          <Input label={t('drawtool.sidepanel.input.description')} value={name} onChange={handleChange} />
        </div>
      </form>
      <Button isBlock buttonType="error" onClick={onDelete}>
        {t('drawtool.sidepanel.button.delete')}
      </Button>
    </>
  );
};

export default DrawToolUpdateContainer;
